import { motion, useInView } from "framer-motion";
import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../../components/feature/Navbar";
import Footer from "../../../components/feature/Footer";
import useSEO from "../../../hooks/useSEO";

const VoiceSearchSEO = () => {
  const heroRef = useRef(null);
  const isHeroInView = useInView(heroRef, { once: true });
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  // SEO dinámico
  useSEO({
    title: "Voice Search SEO - Optimización para Búsquedas por Voz | SEOgenia",
    description: "Optimiza tu web para Alexa, Siri, Google Assistant y búsquedas por voz. Lenguaje natural, featured snippets y respuestas directas que convierten voz en tráfico.",
    keywords: "voice search seo, búsqueda por voz, alexa seo, google assistant, siri, optimización voz",
    canonical: "https://seogenia.es/servicios/voice-search-seo",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Voice Search SEO",
      "description": "Optimización para búsquedas por voz en Alexa, Siri, Google Assistant",
      "provider": {
        "@type": "Organization",
        "name": "SEOgenia",
        "url": "https://seogenia.es"
      },
      "areaServed": "ES",
      "serviceType": "Voice Search Optimization"
    }
  });

  const faqs = [
    {
      question: "¿Por qué es importante optimizar para búsquedas por voz?",
      answer: "Las búsquedas por voz representan ya el 27% de todas las búsquedas online y crecen un 35% anualmente. Para 2025, se estima que el 50% de las búsquedas serán por voz. Los usuarios de voz tienen intención de compra más alta (búsquedas locales, urgentes, específicas). Si no optimizas para voz, pierdes acceso a este segmento de alto valor que solo escucha UNA respuesta, no 10 resultados."
    },
    {
      question: "¿Qué diferencia hay entre búsqueda por texto y búsqueda por voz?",
      answer: "Las búsquedas por texto son cortas y con keywords ('restaurante italiano Madrid'). Las búsquedas por voz son conversacionales y completas ('¿dónde hay un buen restaurante italiano cerca de mí que esté abierto ahora?'). La voz usa preguntas naturales (quién, qué, dónde, cuándo, por qué, cómo), tiene intención local más fuerte, y los asistentes solo leen UNA respuesta (posición #0), no múltiples resultados."
    },
    {
      question: "¿Mi negocio local se beneficia más de Voice Search SEO?",
      answer: "Absolutamente. El 58% de las búsquedas por voz son locales ('cerca de mí', 'abierto ahora', 'cómo llegar'). Los usuarios de voz buscan negocios locales 3x más que en búsquedas por texto. Si tienes un negocio físico (restaurante, tienda, clínica, taller), Voice Search SEO es crítico. Optimizamos tu Google Business Profile, añadimos Schema LocalBusiness, y estructuramos contenido para responder preguntas locales específicas."
    },
    {
      question: "¿Cómo puedo medir si estoy recibiendo tráfico de búsquedas por voz?",
      answer: "Google Analytics no distingue directamente voz vs texto, pero hay indicadores: búsquedas de long-tail muy específicas y conversacionales, tráfico móvil con queries en formato pregunta, visitas desde featured snippets (posición #0), y tráfico de 'near me' searches. Usamos herramientas especializadas que rastrean rankings en búsquedas conversacionales y monitoreamos qué queries de voz están trayendo tráfico a tu sitio."
    },
    {
      question: "¿Necesito crear contenido nuevo para Voice Search?",
      answer: "No necesariamente desde cero. Primero reestructuramos tu contenido existente en formato FAQ (pregunta-respuesta directa), añadimos lenguaje conversacional natural, y optimizamos para featured snippets. Luego identificamos preguntas que tu audiencia hace por voz pero tú no respondes, y creamos contenido específico para esas queries. La clave es formato conversacional, respuestas concisas (29 palabras promedio), y estructura clara."
    },
    {
      question: "¿Qué asistentes de voz son más importantes para mi negocio?",
      answer: "Depende de tu mercado. En España: Google Assistant domina con 60% de cuota (Android, Google Home), Siri tiene 25% (iPhone, iPad), Alexa 10% (Echo devices), y Cortana/otros 5%. Optimizamos principalmente para Google Assistant y Siri, que cubren el 85% del mercado. Si vendes productos compatibles con Alexa (smart home, skills), también optimizamos específicamente para el ecosistema Amazon."
    },
    {
      question: "¿Voice Search SEO afecta mi posicionamiento en búsquedas normales?",
      answer: "Sí, positivamente. Las técnicas de Voice Search SEO (contenido conversacional, FAQs, respuestas directas, featured snippets) también mejoran tu SEO tradicional. Los featured snippets que capturamos para voz también aparecen en búsquedas por texto. El contenido en formato pregunta-respuesta aumenta tu relevancia para long-tail keywords. Es una estrategia win-win que mejora ambos canales simultáneamente."
    },
    {
      question: "¿Cuánto tiempo tarda en posicionarse para búsquedas por voz?",
      answer: "Los featured snippets (posición #0, esencial para voz) pueden aparecer en 3-6 semanas si ya tienes autoridad de dominio. Para sitios nuevos o con baja autoridad, toma 2-4 meses. La optimización completa para Voice Search (incluyendo rankings consistentes en múltiples queries conversacionales) requiere 3-6 meses. Los resultados son acumulativos: cuanto más tiempo optimizado, más queries de voz capturamos."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <Navbar scrolled={true} />

      {/* Hero Section */}
      <section
        ref={heroRef}
        className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-slate-900"
      >
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="inline-flex items-center space-x-2 bg-white/10 px-4 py-2 rounded-full shadow-sm mb-6">
              <i className="ri-mic-line text-cyan-400"></i>
              <span className="text-sm font-medium text-white">Búsqueda del Futuro</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Voice Search SEO
            </h1>
            <p className="text-lg sm:text-xl text-gray-300 mb-8 leading-relaxed">
              Optimiza tu web para búsquedas por voz en Alexa, Siri, Google Assistant y todos los asistentes virtuales. El 50% de las búsquedas ya son por voz.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="#contacto"
                className="px-8 py-3 bg-cyan-500 text-white rounded-full font-medium hover:shadow-lg transition-all duration-300 whitespace-nowrap"
              >
                Optimizar para Voz
              </a>
              <a
                href="#estrategias"
                className="px-8 py-3 bg-white/10 text-white rounded-full font-medium hover:shadow-lg transition-all duration-300 border border-white/20 whitespace-nowrap"
              >
                Ver Técnicas
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-16">
            {[
              { stat: "50%", label: "Búsquedas por voz en 2025" },
              { stat: "71%", label: "Prefieren voz a escribir" },
              { stat: "3x", label: "Más búsquedas locales" },
              { stat: "58%", label: "Buscan negocios locales" }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-5xl font-bold bg-gradient-to-r from-cyan-600 to-pink-600 bg-clip-text text-transparent mb-2">
                  {item.stat}
                </div>
                <p className="text-gray-600">{item.label}</p>
              </motion.div>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                ¿Por qué Voice Search SEO?
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Las búsquedas por voz son fundamentalmente diferentes a las escritas. La gente habla de forma natural, hace preguntas completas y espera respuestas directas e inmediatas.
              </p>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                "OK Google, ¿dónde está la mejor pizzería cerca de mí?" es muy diferente a escribir "pizzería Madrid". Tu contenido debe estar optimizado para este lenguaje conversacional.
              </p>
              <div className="space-y-4">
                {[
                  "Búsquedas más largas y conversacionales",
                  "Mayor intención de compra inmediata",
                  "Enfoque en resultados locales",
                  "Respuestas directas y concisas"
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center">
                    <i className="ri-checkbox-circle-fill text-green-500 text-xl mr-3"></i>
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <img
                src="https://public.readdy.ai/ai/img_res/1747ddcc92ac43bdfef1c6cfe2fed93e.jpg"
                alt="Voice Search SEO"
                className="w-full h-auto rounded-2xl shadow-2xl object-cover object-top"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Voice Assistants Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Asistentes de Voz que Optimizamos
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Tu negocio visible en todos los asistentes virtuales principales
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                name: "Google Assistant",
                users: "500M+ dispositivos",
                description: "El asistente más usado en Android y Google Home, integrado con Maps y Search",
                icon: "ri-google-fill",
                color: "from-blue-500 to-green-500"
              },
              {
                name: "Alexa",
                users: "200M+ dispositivos",
                description: "Líder en smart speakers con Echo, integrado con Amazon y servicios locales",
                icon: "ri-amazon-fill",
                color: "from-cyan-500 to-blue-600"
              },
              {
                name: "Siri",
                users: "500M+ dispositivos",
                description: "Presente en todos los dispositivos Apple, integrado con Apple Maps",
                icon: "ri-apple-fill",
                color: "from-gray-600 to-gray-800"
              },
              {
                name: "Cortana",
                users: "150M+ usuarios",
                description: "Asistente de Microsoft integrado en Windows y Office 365",
                icon: "ri-microsoft-fill",
                color: "from-blue-600 to-cyan-500"
              },
              {
                name: "Bixby",
                users: "100M+ dispositivos",
                description: "Asistente de Samsung presente en smartphones y electrodomésticos",
                icon: "ri-smartphone-line",
                color: "from-purple-500 to-pink-600"
              },
              {
                name: "Meta AI",
                users: "50M+ usuarios",
                description: "Asistente de voz en WhatsApp, Instagram y dispositivos Meta",
                icon: "ri-meta-fill",
                color: "from-pink-500 to-purple-600"
              }
            ].map((assistant, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className={`w-14 h-14 bg-gradient-to-br ${assistant.color} rounded-xl flex items-center justify-center mb-6`}>
                  <i className={`${assistant.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{assistant.name}</h3>
                <div className="inline-block px-3 py-1 bg-gradient-to-r from-cyan-100 to-pink-100 text-cyan-700 rounded-full text-xs font-medium mb-4">
                  {assistant.users}
                </div>
                <p className="text-gray-600 leading-relaxed">{assistant.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Strategies Section */}
      <section id="estrategias" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Estrategias de Optimización
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Técnicas específicas para dominar las búsquedas por voz
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: "Lenguaje Natural y Conversacional",
                description: "Optimizamos tu contenido para preguntas completas y lenguaje cotidiano",
                points: [
                  "Preguntas con quién, qué, dónde, cuándo, por qué, cómo",
                  "Frases largas de 3-5 palabras o más",
                  "Tono conversacional y cercano",
                  "Respuestas en primera persona cuando sea apropiado",
                  "Vocabulario simple y directo"
                ],
                icon: "ri-chat-voice-line",
                color: "cyan"
              },
              {
                title: "Featured Snippets Optimization",
                description: "Posicionamos tu contenido en la posición #0 para respuestas de voz",
                points: [
                  "Respuestas directas de 40-60 palabras",
                  "Formato de párrafo, lista o tabla según la pregunta",
                  "Definiciones claras al inicio del contenido",
                  "Uso de etiquetas de encabezado apropiadas",
                  "Estructura de pregunta-respuesta explícita"
                ],
                icon: "ri-trophy-line",
                color: "purple"
              },
              {
                title: "SEO Local Avanzado",
                description: "El 58% de búsquedas por voz son locales, optimizamos tu presencia",
                points: [
                  "Google Business Profile completamente optimizado",
                  "NAP (Nombre, Dirección, Teléfono) consistente",
                  "Palabras clave locales + 'cerca de mí'",
                  "Reseñas y valoraciones actualizadas",
                  "Contenido específico de ubicación"
                ],
                icon: "ri-map-pin-line",
                color: "pink"
              },
              {
                title: "Schema Markup para Voz",
                description: "Datos estructurados específicos que los asistentes pueden leer",
                points: [
                  "Speakable schema para contenido leíble por voz",
                  "FAQPage schema para preguntas frecuentes",
                  "LocalBusiness schema con horarios y ubicación",
                  "HowTo schema para instrucciones paso a paso",
                  "Event schema para eventos y actividades"
                ],
                icon: "ri-code-s-slash-line",
                color: "cyan"
              },
              {
                title: "Velocidad y Mobile-First",
                description: "El 90% de búsquedas por voz son desde móviles, optimizamos rendimiento",
                points: [
                  "Tiempo de carga menor a 3 segundos",
                  "Diseño responsive perfecto en móviles",
                  "AMP (Accelerated Mobile Pages) cuando sea apropiado",
                  "Imágenes optimizadas y lazy loading",
                  "Core Web Vitals en verde"
                ],
                icon: "ri-speed-line",
                color: "purple"
              },
              {
                title: "Contenido de Formato Largo",
                description: "Los artículos extensos tienen 40% más probabilidad de aparecer en voz",
                points: [
                  "Artículos de 2000+ palabras bien estructurados",
                  "Múltiples preguntas respondidas en un solo contenido",
                  "Tabla de contenidos con enlaces internos",
                  "Secciones claramente diferenciadas",
                  "Información completa y exhaustiva"
                ],
                icon: "ri-article-line",
                color: "pink"
              }
            ].map((strategy, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-8 rounded-2xl shadow-lg"
              >
                <div className={`w-14 h-14 bg-gradient-to-br from-${strategy.color}-500 to-${strategy.color}-600 rounded-xl flex items-center justify-center mb-6`}>
                  <i className={`${strategy.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">{strategy.title}</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">{strategy.description}</p>
                <ul className="space-y-2">
                  {strategy.points.map((point, idx) => (
                    <li key={idx} className="flex items-start">
                      <i className="ri-arrow-right-s-line text-cyan-600 mt-1 mr-2 flex-shrink-0"></i>
                      <span className="text-gray-600 text-sm">{point}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Nuestro Proceso
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Metodología completa para dominar las búsquedas por voz
            </p>
          </motion.div>

          <div className="space-y-6">
            {[
              {
                step: "1",
                title: "Análisis de Búsquedas por Voz",
                description: "Investigamos qué preguntas hace tu audiencia por voz y qué asistentes usan más",
                icon: "ri-search-line"
              },
              {
                step: "2",
                title: "Optimización de Contenido",
                description: "Reescribimos y estructuramos tu contenido para lenguaje natural y respuestas directas",
                icon: "ri-edit-line"
              },
              {
                step: "3",
                title: "Implementación Técnica",
                description: "Aplicamos schema markup, mejoramos velocidad y optimizamos para móviles",
                icon: "ri-tools-line"
              },
              {
                step: "4",
                title: "SEO Local",
                description: "Optimizamos tu presencia local en Google Business y directorios relevantes",
                icon: "ri-map-pin-2-line"
              },
              {
                step: "5",
                title: "Testing y Monitoreo",
                description: "Probamos con asistentes reales y monitoreamos rankings en búsquedas por voz",
                icon: "ri-line-chart-line"
              }
            ].map((process, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="flex gap-6 items-start"
              >
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-pink-500 rounded-2xl flex items-center justify-center">
                    <span className="text-2xl font-bold text-white">{process.step}</span>
                  </div>
                </div>
                <div className="flex-1 bg-white p-8 rounded-2xl shadow-lg">
                  <div className="flex items-center mb-3">
                    <i className={`${process.icon} text-2xl text-purple-600 mr-3`}></i>
                    <h3 className="text-2xl font-bold text-gray-900">{process.title}</h3>
                  </div>
                  <p className="text-lg text-gray-600 leading-relaxed">{process.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Dominando la Búsqueda por Voz
            </h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto">
              Negocios que capturaron tráfico de asistentes virtuales
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {[
              {
                name: "Antonio Jiménez",
                company: "Restaurante La Tradición",
                role: "Propietario",
                text: "Ahora aparecemos en todas las búsquedas por voz de 'restaurante cerca de mí'. Las reservas desde Google Assistant aumentaron un 280%.",
                rating: 5
              },
              {
                name: "Isabel Martín",
                company: "Clínica Fisioterapia Salud",
                role: "Directora",
                text: "Optimizamos para Siri y Alexa. Las llamadas de nuevos pacientes que nos encontraron por voz se cuadruplicaron en 3 meses.",
                rating: 5
              }
            ].map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gray-800 p-8 rounded-2xl border border-gray-700"
              >
                <div className="flex items-center gap-2 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-lg text-white leading-relaxed mb-6 italic">
                  "{testimonial.text}"
                </p>
                <div>
                  <p className="font-bold text-white text-lg">
                    {testimonial.name}
                  </p>
                  <p className="text-gray-300">
                    {testimonial.role} en {testimonial.company}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Preguntas Frecuentes
            </h2>
            <p className="text-lg text-gray-600">
              Resolvemos tus dudas sobre Voice Search SEO
            </p>
          </motion.div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-lg transition-all duration-300"
              >
                <button
                  onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                  className="w-full px-8 py-6 flex items-center justify-between text-left hover:bg-gray-50 transition-colors duration-200"
                >
                  <h3 className="text-lg font-bold text-gray-900 pr-8">
                    {faq.question}
                  </h3>
                  <i
                    className={`ri-arrow-down-s-line text-2xl text-orange-600 flex-shrink-0 transition-transform duration-300 ${
                      openFAQ === index ? "rotate-180" : ""
                    }`}
                  ></i>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openFAQ === index ? "max-h-[600px]" : "max-h-0"
                  }`}
                >
                  <div className="px-8 pb-6">
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-purple-600">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Domina las Búsquedas por Voz
            </h2>
            <p className="text-lg text-white/90 mb-8 leading-relaxed">
              Optimiza para Alexa, Siri, Google Assistant y el futuro de la búsqueda
            </p>
            <Link
              to="/#contacto"
              className="inline-block px-8 py-4 bg-white text-gray-900 rounded-full font-bold hover:shadow-2xl transition-all duration-300 whitespace-nowrap"
            >
              Solicitar Optimización Voice Search
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            ¿Listo para dominar Voice Search?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Optimiza tu contenido para búsquedas por voz y asistentes virtuales
          </p>
          <Link 
            to="/#contacto"
            className="inline-block px-8 py-4 bg-cyan-500 text-white rounded-full font-medium text-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 whitespace-nowrap"
          >
            Solicitar Consulta Gratuita
          </Link>
        </div>
      </section>

      {/* Servicios Relacionados */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Servicios Complementarios
            </h2>
            <p className="text-xl text-gray-600">
              Potencia tu SEO con estos servicios relacionados
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* SEO Agentes IA */}
            <Link 
              to="/servicios/seo-agentes-ia"
              className="group bg-gradient-to-br from-slate-50 to-gray-50 rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 border border-gray-200 hover:border-cyan-400"
            >
              <div className="w-16 h-16 bg-gradient-to-r from-cyan-400 to-pink-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <i className="ri-robot-2-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-cyan-400 transition-colors">
                SEO para Agentes IA
              </h3>
              <p className="text-gray-600 mb-4">
                Las búsquedas por voz son procesadas por agentes IA. Optimiza tu contenido para ambos canales simultáneamente.
              </p>
              <div className="flex items-center text-cyan-400 font-medium group-hover:gap-3 transition-all">
                <span>Descubrir más</span>
                <i className="ri-arrow-right-line group-hover:translate-x-2 transition-transform"></i>
              </div>
            </Link>

            {/* Schema Markup */}
            <Link 
              to="/servicios/schema-markup-avanzado"
              className="group bg-gradient-to-br from-slate-50 to-gray-50 rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 border border-gray-200 hover:border-cyan-400"
            >
              <div className="w-16 h-16 bg-gradient-to-r from-cyan-400 to-pink-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <i className="ri-code-s-slash-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-cyan-400 transition-colors">
                Schema Markup Avanzado
              </h3>
              <p className="text-gray-600 mb-4">
                Los asistentes de voz leen Schema Markup para responder preguntas. Implementa SpeakableSchema para destacar.
              </p>
              <div className="flex items-center text-cyan-400 font-medium group-hover:gap-3 transition-all">
                <span>Descubrir más</span>
                <i className="ri-arrow-right-line group-hover:translate-x-2 transition-transform"></i>
              </div>
            </Link>

            {/* Progressive Web App */}
            <Link 
              to="/servicios/progressive-web-app"
              className="group bg-gradient-to-br from-slate-50 to-gray-50 rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 border border-gray-200 hover:border-cyan-400"
            >
              <div className="w-16 h-16 bg-gradient-to-r from-cyan-400 to-pink-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <i className="ri-smartphone-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-cyan-400 transition-colors">
                Progressive Web App
              </h3>
              <p className="text-gray-600 mb-4">
                Las búsquedas por voz ocurren principalmente en móviles. Una PWA rápida mejora tu posicionamiento.
              </p>
              <div className="flex items-center text-cyan-400 font-medium group-hover:gap-3 transition-all">
                <span>Descubrir más</span>
                <i className="ri-arrow-right-line group-hover:translate-x-2 transition-transform"></i>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default VoiceSearchSEO;